import { Fragment } from 'react'
import { motion } from 'framer-motion'
import { clsx } from 'clsx'

const TONE = {
  slate:   { value: 'text-slate-200',   icon: 'text-slate-400',   bg: 'bg-slate-500/20' },
  danger:  { value: 'text-red-400',     icon: 'text-red-400',     bg: 'bg-red-500/15' },
  success: { value: 'text-emerald-400', icon: 'text-emerald-400', bg: 'bg-emerald-500/15' },
  warning: { value: 'text-amber-400',   icon: 'text-amber-400',   bg: 'bg-amber-500/15' },
  accent:  { value: 'text-cyan-400',    icon: 'text-cyan-400',    bg: 'bg-cyan-500/15' },
  purple:  { value: 'text-purple-400',  icon: 'text-purple-400',  bg: 'bg-purple-500/15' },
}

function pickTone(tone) {
  return TONE[tone] || TONE.slate
}

function StatItem({ item }) {
  const tone = pickTone(item.tone)
  const Icon = item.icon
  return (
    <div className="flex items-center justify-center gap-2.5 sm:flex-1 min-w-0">
      {Icon && (
        <div className={clsx('w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0', tone.bg)}>
          <Icon size={14} className={tone.icon} />
        </div>
      )}
      <div className="flex items-baseline gap-1.5 whitespace-nowrap">
        <span className={clsx('text-lg font-black tabular-nums leading-none', tone.value)}>{item.value}</span>
        <span className="text-[11px] font-medium uppercase tracking-wide text-slate-500">{item.label}</span>
      </div>
    </div>
  )
}

/**
 * Summary stats strip — a single condensed row inside one glass-card, at every
 * width. Each stat shows a tinted icon chip (when an `icon` is given), the value,
 * and a small label; stats are separated by thin dividers and wrap to a second
 * line rather than horizontal-scrolling on a tight viewport.
 *
 * It's a glanceable summary, not a dashboard: `sub` captions are ignored. (This
 * previously rendered tall KPI cards at xl+; those were dropped — they cost more
 * vertical space than they earned, and the table is the page's focus.)
 *
 * items: [{ label, value, icon?, tone? }]
 *   tone — slate | danger | success | warning | accent | purple (default slate)
 * className?: string — extra classes on the strip card
 */
export default function StatStrip({ items, className = '' }) {
  if (!items || items.length === 0) return null

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25 }}
      className={clsx(
        'glass-card px-4 py-2.5 mb-4 flex flex-wrap items-center gap-x-4 gap-y-2',
        className,
      )}
    >
      {items.map((item, i) => (
        <Fragment key={i}>
          {i > 0 && <div className="hidden sm:block w-px h-7 bg-white/10 flex-none" aria-hidden="true" />}
          <StatItem item={item} />
        </Fragment>
      ))}
    </motion.div>
  )
}
